/* NutriFitt category pages: sort, quick add with flavor picker, card entrance. */
(() => {
  const addUrl = () => (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';

  const addToCart = async id => {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || (data.status && data.status !== 200)) throw new Error(data.description || data.message || 'Unable to add');
    document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
    if (window.theme && theme.settings && theme.settings.cart_type === 'drawer' && document.querySelector('.cart-drawer')) {
      document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false }));
    }
  };

  const setupCard = (form, signal) => {
    const opener = form.querySelector('[data-nfcat-open]');
    const picker = form.querySelector('.nfcat__picker');
    const main = opener || form.querySelector('.nfcat__button');
    const label = main && main.querySelector('[data-nfcat-label]');
    const status = form.querySelector('[data-nfcat-status]');
    const original = label ? label.textContent : '';

    const close = (restoreFocus = true) => {
      if (!picker || picker.hidden) return;
      picker.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      if (restoreFocus) opener.focus();
    };
    const open = () => {
      document.querySelectorAll('.nfcat__picker:not([hidden])').forEach(other => {
        const otherOpener = other.closest('form').querySelector('[data-nfcat-open]');
        other.hidden = true;
        if (otherOpener) otherOpener.setAttribute('aria-expanded', 'false');
      });
      picker.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      const first = picker.querySelector('.nfcat__flavor');
      if (first) first.focus({ preventScroll: true });
    };

    if (opener && picker) {
      opener.addEventListener('click', event => { event.preventDefault(); picker.hidden ? open() : close(); }, { signal });
      opener.addEventListener('keydown', event => { if (event.key === ' ') { event.preventDefault(); open(); } }, { signal });
      picker.querySelector('[data-nfcat-close]').addEventListener('click', () => close(), { signal });
      picker.addEventListener('keydown', event => { if (event.key === 'Escape') close(); }, { signal });
      document.addEventListener('click', event => { if (!picker.hidden && !form.contains(event.target)) close(false); }, { signal });
    }

    form.addEventListener('submit', async event => {
      if (!window.fetch) return;
      event.preventDefault();
      const submitter = event.submitter;
      const id = Number((submitter && submitter.name === 'id' && submitter.value) || new FormData(form).get('id'));
      if (!id || form.getAttribute('aria-busy') === 'true') return;
      form.setAttribute('aria-busy', 'true');
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      if (main) main.setAttribute('aria-busy', 'true');
      try {
        await addToCart(id);
        if (picker) close(false);
        if (main) main.classList.add('is-added');
        if (label) label.textContent = 'Added';
        if (status) status.textContent = 'Added to cart';
        setTimeout(() => {
          if (main) main.classList.remove('is-added');
          if (label) label.textContent = original;
          if (status) status.textContent = '';
        }, 2200);
      } catch (error) {
        window.location.href = form.dataset.productUrl + '?variant=' + id;
      } finally {
        form.removeAttribute('aria-busy');
        if (submitter) submitter.removeAttribute('aria-busy');
        if (main) main.removeAttribute('aria-busy');
      }
    }, { signal });
  };

  const init = (root = document) => root.querySelectorAll('[data-nf-category]').forEach(page => {
    if (page.dataset.ready) return;
    page.dataset.ready = 'true';
    const abort = new AbortController();
    const signal = abort.signal;

    const sort = page.querySelector('[name=sort_by]');
    if (sort) sort.addEventListener('change', () => (sort.form.requestSubmit ? sort.form.requestSubmit() : sort.form.submit()), { signal });

    page.querySelectorAll('[data-nfcat-buy]').forEach(form => setupCard(form, signal));

    let observer;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if ('IntersectionObserver' in window && !reduced && !(window.Shopify && Shopify.designMode)) {
      const cards = [...page.querySelectorAll('.nfcat__card')];
      observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        card.classList.add('is-entering');
        requestAnimationFrame(() => card.classList.remove('is-waiting'));
        setTimeout(() => card.classList.remove('is-entering'), 1000);
        observer.unobserve(card);
      }), { threshold: 0.05, rootMargin: '0px 0px -30px 0px' });
      cards.forEach(card => {
        if (card.getBoundingClientRect().top > innerHeight) card.classList.add('is-waiting');
        observer.observe(card);
      });
    }

    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(page)) { abort.abort(); if (observer) observer.disconnect(); }
    }, { signal });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* Product covers: every render is uploaded with different padding, so measure the
   visible product in each image and scale it to one shared size and baseline. */
(() => {
  const FIT = { height: 0.72, width: 0.74, floor: 0.86 };
  const boxes = new Map();

  const measure = image => {
    const side = 160;
    const ratio = Math.min(1, side / Math.max(image.naturalWidth, image.naturalHeight));
    const w = Math.max(1, Math.round(image.naturalWidth * ratio));
    const h = Math.max(1, Math.round(image.naturalHeight * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, w, h);
    const data = context.getImageData(0, 0, w, h).data;
    const corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    const clear = corners.filter(i => data[i + 3] < 20).length >= 2;
    const bg = [0, 1, 2].map(k => corners.reduce((sum, i) => sum + data[i + k], 0) / 4);
    const rows = new Uint16Array(h), cols = new Uint16Array(w);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const ink = clear ? data[i + 3] > 40 : data[i + 3] > 40 && Math.max(Math.abs(data[i] - bg[0]), Math.abs(data[i + 1] - bg[1]), Math.abs(data[i + 2] - bg[2])) > 26;
      if (ink) { rows[y]++; cols[x]++; }
    }
    const minRow = Math.max(1, w * 0.02), minCol = Math.max(1, h * 0.02);
    const top = rows.findIndex(v => v >= minRow), left = cols.findIndex(v => v >= minCol);
    if (top < 0 || left < 0) return null;
    let bottom = h - 1; while (bottom > top && rows[bottom] < minRow) bottom--;
    let right = w - 1; while (right > left && cols[right] < minCol) right--;
    return { x: left / w, y: top / h, w: (right - left + 1) / w, h: (bottom - top + 1) / h, tile: clear ? '' : 'rgb(' + bg.map(Math.round).join(',') + ')' };
  };

  const fit = image => {
    if (!image.complete || !image.naturalWidth) return;
    const key = (image.currentSrc || image.src).split('?')[0];
    if (!boxes.has(key)) { let box = null; try { box = measure(image); } catch (error) {} boxes.set(key, box); }
    const box = boxes.get(key);
    const media = image.offsetParent;
    const bw = image.offsetWidth, bh = image.offsetHeight;
    if (box && media && bw && bh) {
      const mw = media.clientWidth, mh = media.clientHeight;
      const s0 = Math.min(bw / image.naturalWidth, bh / image.naturalHeight);
      const rw = image.naturalWidth * s0, rh = image.naturalHeight * s0;
      const x0 = image.offsetLeft + (bw - rw) / 2 + box.x * rw, y0 = image.offsetTop + (bh - rh) / 2 + box.y * rh;
      const cw = box.w * rw, ch = box.h * rh;
      const scale = Math.min(2.6, Math.max(0.5, Math.min(FIT.height * mh / ch, FIT.width * mw / cw)));
      const ox = image.offsetLeft + bw / 2, oy = image.offsetTop + bh / 2;
      const tx = mw / 2 - ox - scale * (x0 + cw / 2 - ox);
      const ty = FIT.floor * mh - oy - scale * (y0 + ch - oy);
      image.style.setProperty('--fit-s', scale.toFixed(4));
      image.style.setProperty('--fit-x', (tx / bw * 100).toFixed(3) + '%');
      image.style.setProperty('--fit-y', (ty / bh * 100).toFixed(3) + '%');
      if (box.tile) media.style.backgroundColor = box.tile;
    }
    image.classList.add('is-fit');
  };

  const init = (root = document) => {
    const images = [...root.querySelectorAll('[data-nf-category] .nfcat__img:not(.nfcat__img--hover)')];
    images.forEach(image => {
      if (image.dataset.fit) return;
      image.dataset.fit = 'true';
      image.closest('[data-nf-category]').classList.add('nfcat--fit');
      image.addEventListener('load', () => fit(image));
      image.addEventListener('error', () => image.classList.add('is-fit'));
      fit(image);
    });
  };
  let frame = 0;
  addEventListener('resize', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => document.querySelectorAll('.nfcat__img.is-fit').forEach(fit)); }, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
